import { FacetResponseType, ProgrammeSearchInput, ProgrammeSortType, TextSearchAlgorithmType, } from '@studyportals/search-api-interface';
import { ParameterType, SortingType } from '../enums';
import { FacetValue, FilterFacets, ParameterValue, DURATION_FILTER_BUCKETS } from '../domain';
import { countriesAll } from '@studyportals/static-domain-data/countries/countries';
import { countryGetIsoCode } from '@studyportals/static-domain-data/countries/country-get-iso-code';
import { countryGetByIsoCode } from '@studyportals/static-domain-data/countries/country-get-by-iso-code';
import { AttendanceFilterOptionValue, DegreeTypeFilterOptionValue, DurationFilterOptionValue, EducationalFormFilterOptionValue, EducationLevelFilterOptionValue, FacetOptionValue, FilterKey, FormatFilterOptionValue, SpecialProgrammesFilterOptionValue } from '@studyportals/search-filters/server-side';
import { PortalType } from '@studyportals/domain-client';

/**
 * This is here to provide a compatibility layer between the old and new Search API
 * It will be removed once the old API is no longer in use in any portal
 * */
export default class CompatibilityLayer {
	static mapToProgrammeSearchInput(parametersValue: ParameterValue[],
		sorting: SortingType,
		portalType: PortalType,
		facets?: FacetOptionValue[]
	): ProgrammeSearchInput {
		const tuitionFee = CompatibilityLayer.mapParametersToFlatArray<string>(parametersValue, ParameterType.TUITION_FEE);
		const { tuitionFeeMin, tuitionFeeMax } = CompatibilityLayer.mapTuitionFee(tuitionFee);

		return {
			attendances: CompatibilityLayer.mapParametersToFlatArray<AttendanceFilterOptionValue>(parametersValue, ParameterType.DELIVERY_METHOD),
			degreeTypes: CompatibilityLayer.mapParametersToFlatArray<DegreeTypeFilterOptionValue>(parametersValue, ParameterType.DEGREE_TYPE),
			disciplines: CompatibilityLayer.mapParametersToFlatArray<number>(parametersValue, ParameterType.DISCIPLINES),
			durations: CompatibilityLayer.mapParametersToFlatArray<DurationFilterOptionValue>(parametersValue, ParameterType.DURATION),
			educationalLevels: CompatibilityLayer.mapParametersToFlatArray<EducationLevelFilterOptionValue>(parametersValue, ParameterType.EDUCATION_LEVEL),
			text: CompatibilityLayer.mapTextFromParameters(parametersValue),
			areaIds: CompatibilityLayer.mapParametersToFlatArray<number>(parametersValue, ParameterType.AREA),
			regionIds: CompatibilityLayer.mapParametersToFlatArray<number>(parametersValue, ParameterType.CONTINENT),
			countryIsoCodes: CompatibilityLayer.mapCountryIdsToIsoCodes(CompatibilityLayer.mapParametersToFlatArray<string>(parametersValue, ParameterType.COUNTRY)),
			organisationIds: CompatibilityLayer.mapParametersToFlatArray<number>(parametersValue, ParameterType.ORGANISATIONS),
			specialProgrammes: CompatibilityLayer.mapParametersToFlatArray<SpecialProgrammesFilterOptionValue>(parametersValue, ParameterType.SPECIAL_PROGRAMMES),
			tuitionFeeMin,
			tuitionFeeMax,
			nationalityCountryIso: CompatibilityLayer.getNationalityFromParameters(parametersValue),
			densities: CompatibilityLayer.mapParametersToFlatArray<FormatFilterOptionValue>(parametersValue, ParameterType.ATTENDANCE),
			sortBy: CompatibilityLayer.mapSortingType(sorting),
			educationalForms: CompatibilityLayer.mapParametersToFlatArray<EducationalFormFilterOptionValue>(parametersValue, ParameterType.EDUCATIONAL_FORM),
			campaignTypes: CompatibilityLayer.mapParametersToFlatArray<string>(parametersValue, ParameterType.CAMPAIGN_TYPE),

			facets: facets || [
				FacetOptionValue.AREA_IDS,
				FacetOptionValue.COUNTRY_ISO_CODES,
				FacetOptionValue.ATTENDANCES,
				FacetOptionValue.DEGREE_TYPES,
				FacetOptionValue.DISCIPLINES,
				FacetOptionValue.DURATIONS,
				FacetOptionValue.EDUCATIONAL_FORMS,
				FacetOptionValue.EDUCATIONAL_LEVEL,
				FacetOptionValue.REGION_IDS,
				FacetOptionValue.DENSITIES,
				FacetOptionValue.SPECIAL_PROGRAMMES,
			],
			facetsDurations: DURATION_FILTER_BUCKETS[portalType],
			textSearchAlgorithm: TextSearchAlgorithmType.HYBRID
		};
	}

	static mapFacetsToFilterFacets(facets: FacetResponseType[]): FilterFacets[] {
		const filterFacets: FilterFacets[] = [];

		for (const { filter, values } of facets) {
			const filterParamType = CompatibilityLayer.getFilterParamTypeFromFacetResponseType({ filter });
			if (filterParamType) {
				const filters = CompatibilityLayer.getFilterValueFromFacetResponseType({ values, filter }, filterParamType);

				filterFacets.push(new FilterFacets(filterParamType, filters));
			}
		}

		return filterFacets;
	}

	static mapFilterKeyToParameterType(key: FilterKey): ParameterType {
		const possibleMatch = key as unknown as ParameterType;
		if (Object.values(ParameterType).includes(possibleMatch)) {
			return possibleMatch;
		}
		throw new Error(`Could not convert FilterKey ${key} to ParameterType`);
	}

	private static mapTuitionFee(tuition: string[]): { tuitionFeeMin?: number, tuitionFeeMax?: number; } {
		if (!tuition.length) return { tuitionFeeMin: undefined, tuitionFeeMax: undefined };

		try {
			const tuitionFee = tuition[0];
			const [min, max] = JSON.parse(tuitionFee) as [number, number];

			return {
				tuitionFeeMin: min === -1 ? undefined : min,
				tuitionFeeMax: max === -1 ? undefined : max
			};
		} catch {
			return { tuitionFeeMin: undefined, tuitionFeeMax: undefined };
		}
	}

	private static mapTextFromParameters(parameters: ParameterValue[]): string {
		const kw = CompatibilityLayer.mapParametersToFlatArray<string>(parameters, ParameterType.KEYWORD);
		const kwWhat = CompatibilityLayer.mapParametersToFlatArray<string>(parameters, ParameterType.KEYWORD_WHAT);
		const kwWhere = CompatibilityLayer.mapParametersToFlatArray<string>(parameters, ParameterType.KEYWORD_WHERE);

		let text = '';
		if (kw.length) {
			text += kw.join(' ');
		}
		if (kwWhat.length) {
			text += ` ${kwWhat.join(' ')}`;
		}
		if (kwWhere.length) {
			text += ` ${kwWhere.join(' ')}`;
		}

		return text;
	}

	private static mapSortingType(sorting: SortingType): ProgrammeSortType {
		switch (sorting) {
			case SortingType.RELEVANCE:
				return ProgrammeSortType.DEFAULT;
			case SortingType.TUITION_FEE_ASC:
				return ProgrammeSortType.TUITION;
			default:
				throw new Error(`Unknown sorting type: ${sorting}`);
		}
	}

	private static getFilterValueFromFacetResponseType({ values }: FacetResponseType, parameterType: ParameterType): FacetValue[] {
		switch (parameterType) {
			case ParameterType.COUNTRY:
				return values.map(value => new FacetValue(countryGetByIsoCode(value.value)!.id, value.count));
			default:
				return values.map(value => new FacetValue(value.value, value.count));
		}
	}

	private static getFilterParamTypeFromFacetResponseType({ filter }: { filter: FacetOptionValue }): ParameterType | undefined {
		switch (filter) {
			case FacetOptionValue.AREA_IDS:
				return ParameterType.AREA;
			case FacetOptionValue.ATTENDANCES:
				return ParameterType.DELIVERY_METHOD;
			case FacetOptionValue.DEGREE_TYPES:
				return ParameterType.DEGREE_TYPE;
			case FacetOptionValue.DISCIPLINES:
				return ParameterType.DISCIPLINES;
			case FacetOptionValue.EDUCATIONAL_LEVEL:
				return ParameterType.EDUCATION_LEVEL;
			case FacetOptionValue.EDUCATIONAL_FORMS:
				return ParameterType.EDUCATIONAL_FORM;
			case FacetOptionValue.REGION_IDS:
				return ParameterType.CONTINENT;
			case FacetOptionValue.SPECIAL_PROGRAMMES:
				return ParameterType.SPECIAL_PROGRAMMES;
			case FacetOptionValue.COUNTRY_ISO_CODES:
				return ParameterType.COUNTRY;
			case FacetOptionValue.DURATIONS:
				return ParameterType.DURATION;
			case FacetOptionValue.DENSITIES:
				return ParameterType.ATTENDANCE;
			default:
				return undefined;
		}
	}

	private static mapParametersToFlatArray<T>(parameters: ParameterValue[], type: ParameterType): T[] {
		return parameters
			.filter(param => param.type === type)
			.map(param => param.value as T)
			.flat() as T[];
	}

	private static getNationalityFromParameters(parametersValue: ParameterValue[]): string | undefined {
		const result = CompatibilityLayer.mapParametersToFlatArray<string>(parametersValue, ParameterType.USER_COUNTRY);
		return result.length ? result[0] : undefined;
	}

	private static mapCountryIdsToIsoCodes(countryIds: string[]): string[] {
		const countriesIsoCodes: string[] = [];

		countryIds.forEach(countryId => {
			const countryFromId = countriesAll.find(country => country.id === countryId);
			if (!countryFromId) return;

			const countryIsoCode = countryGetIsoCode(countryFromId.id);

			if (!countryIsoCode) return;

			countriesIsoCodes.push(countryIsoCode);
		});

		return countriesIsoCodes;
	}
}
